import {
  ROLE,
  SPACE_MEMBER_TYPE,
  SpaceMember,
} from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { Unsubscribe } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import {
  reportMembershipAttach,
  reportMembershipError,
} from "App/modules/membershipTelemetry";

/** One page: the number of rows the dialog can show at once. */
export const ROSTER_PAGE_SIZE = 50;

/**
 * The hard cap on roster pages. The roster loses its incidental 1 MiB size
 * cap (the limit on the space document itself). There is no product answer
 * yet for what a 5,000-member space's Share dialog must be. So: ten pages,
 * then a final row that tells the user the list is cut off. Not
 * search: search over member documents needs an index and a product design,
 * and both come in a later phase.
 */
export const ROSTER_MAX_PAGES = 10;

export type RosterRow = {
  memberId: Id;
  /**
   * The roles after all role math (direct grants plus inheritance). Never
   * compute this from `directRoles`.
   */
  effectiveRoles: ROLE[];
  /**
   * `!!entity.rolesV2[userId]` today. Decides between "Remove User" and
   * "Undo Overrides".
   */
  hasDirectGrant: boolean;
  /**
   * `entity.inheritedRolesV3[userId]` today. Drives the chevron and the
   * block guard.
   */
  inheritedRoles: ROLE[];
  isCreator: boolean;
  type: SPACE_MEMBER_TYPE;
  addedAt: number;
  /**
   * The last write to this one member document. The local-edit overlay uses
   * it as its cutoff.
   */
  updatedAt: number;
};

/**
 * The paginated roster of one space.
 *
 * Two listeners, not one:
 *  - R1, people (`type == 'user'`), attached by anyone who can read the
 *    space;
 *  - R2, pending invites (`type == 'invite'`), attached only for managers.
 *    The security rules grant the invite `list` query to managers only, and
 *    a Firestore `list` fails as a whole. A non-manager that issues it gets
 *    nothing (not a filtered list, nothing) while R1 still succeeds. So an
 *    unconditional R2 gives no partial result. It only adds a permanent
 *    error rate on the exact tag that the on-call alert pages on.
 */
export const useSpaceRoster = ({
  spaceId,
  canManage,
  enabled,
}: {
  spaceId?: Id;
  canManage: boolean;
  enabled: boolean;
}) => {
  const [pages, setPages] = useState<SpaceMember[][]>([]);
  const [invites, setInvites] = useState<SpaceMember[]>([]);
  const [pageCount, setPageCount] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  /*
   * R1: one listener per page. Firestore has no way to extend a live
   * listener. The alternative is to re-issue one query with a `limit` that
   * grows, which is what the sidebar does, and that is quadratic. A roster
   * page is ~15 KB and a space document is up to 1 MiB, so here the
   * tradeoff reverses.
   */
  useEffect(() => {
    if (!enabled || !spaceId) return;
    let cancelled = false;

    /*
     * Warning: page `index`'s `onChange` calls `attach(index + 1, ...)`, and
     * it fires on every snapshot of that page. So a roster that changes
     * often opens one new page-(index+1) listener per change. Each one
     * chains another, and none of them close until teardown. That is an
     * unbounded listener leak on the exact surface this listener-reduction
     * work exists to protect. A manual test does not show it, because
     * last-write-wins keeps the rendered rows correct. `attachedCursors`
     * makes the chain idempotent (safe to run again with the same input): a
     * page attaches once per (index, cursor) pair.
     *
     * Warning: the second half of the fix. The dedupe alone is not enough.
     * Page 0's cursor is its 50th row, so it changes whenever someone adds
     * a member or removes the 50th. That is routine on any space this
     * pagination exists for. On each such change the guard correctly lets a
     * new page-1 listener attach, but the old one is still live and still
     * writes `next[1]`. The result: two listeners with different cursors
     * race over one page index, plus one leaked subscription per change. So
     * we track the unsubscribe per index, and we drop this page's listener
     * and every page after it before we re-attach. Pages beyond a moved
     * cursor are stale by construction.
     */
    const attachedCursors = new Map<number, string>();
    const pageUnsubscribes = new Map<number, Unsubscribe>();

    const dropFrom = (index: number) => {
      Array.from(pageUnsubscribes.keys())
        .filter((i) => i >= index)
        .forEach((i) => {
          pageUnsubscribes.get(i)?.();
          pageUnsubscribes.delete(i);
          attachedCursors.delete(i);
        });
    };

    const attach = (index: number, startAfterMember?: SpaceMember) => {
      if (cancelled || index >= pageCount) return;
      const cursor = startAfterMember
        ? `${startAfterMember.addedAt}:${startAfterMember.memberId}`
        : "";
      if (attachedCursors.get(index) === cursor) return;
      /*
       * A different cursor for an index we already hold means the previous
       * page moved under us. Tear this page and every page after it down
       * first.
       */
      if (attachedCursors.has(index)) dropFrom(index);
      attachedCursors.set(index, cursor);
      reportMembershipAttach("roster");
      pageUnsubscribes.set(
        index,
        highnote.watchSpaceRoster({
          spaceId,
          type: SPACE_MEMBER_TYPE.USER,
          limit: ROSTER_PAGE_SIZE,
          startAfterMember,
          onChange: (members) => {
            if (cancelled) return;
            setError(false);
            setLoading(false);
            setPages((prev) => {
              const next = [...prev];
              next[index] = members;
              return next;
            });
            /*
             * Chain the next page off this page's last row, so the cursor
             * always comes from live data and never from a stale copy.
             */
            if (members.length === ROSTER_PAGE_SIZE) {
              attach(index + 1, members[members.length - 1]);
            }
          },
          onError: (e) => {
            if (cancelled) return;
            /*
             * Do not clear `pages`. An empty roster and a denied roster
             * must not render identically; that is the exact failure this
             * surface must avoid. The caller falls back to the legacy
             * `getFullRoles` list.
             */
            setLoading(false);
            setError(true);
            reportMembershipError("roster", e);
          },
        }),
      );
    };

    setLoading(true);
    attach(0, undefined);

    return () => {
      cancelled = true;
      pageUnsubscribes.forEach((u) => u());
      pageUnsubscribes.clear();
    };
  }, [enabled, spaceId, pageCount]);

  /* R2: pending invites, managers only. */
  useEffect(() => {
    if (!enabled || !spaceId || !canManage) {
      setInvites([]);
      return;
    }
    reportMembershipAttach("roster");
    const unsubscribe = highnote.watchSpaceRoster({
      spaceId,
      type: SPACE_MEMBER_TYPE.INVITE,
      limit: ROSTER_PAGE_SIZE,
      onChange: setInvites,
      onError: (e) => reportMembershipError("roster", e),
    });
    return () => unsubscribe();
  }, [enabled, spaceId, canManage]);

  const rows = useMemo<RosterRow[]>(() => {
    const all = [...pages.flat(), ...invites];
    return all.map((member) => ({
      memberId: member.memberId,
      effectiveRoles: member.effectiveRoles || [],
      hasDirectGrant: member.hasDirectGrant === true,
      inheritedRoles: member.inheritedRoles || [],
      isCreator: member.isCreator === true,
      type: member.type,
      addedAt: member.addedAt,
      updatedAt: member.updatedAt,
    }));
  }, [pages, invites]);

  const rowsByMemberId = useMemo(() => {
    const byId = new Map<Id, RosterRow>();
    rows.forEach((row) => byId.set(row.memberId, row));
    return byId;
  }, [rows]);

  const atCap = pageCount >= ROSTER_MAX_PAGES;
  const hasMore =
    !atCap && (pages[pages.length - 1] || []).length === ROSTER_PAGE_SIZE;

  return {
    rows,
    rowsByMemberId,
    loading,
    error,
    hasMore,
    atCap,
    loadMore: () =>
      setPageCount((prev) => Math.min(prev + 1, ROSTER_MAX_PAGES)),
  };
};
